import React, { useEffect, useState } from 'react';
import axios from 'axios';

const DeckDetail = ({ deckId, onBack }) => {
  const [deck, setDeck] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchDeck = async () => {
    try {
      const res = await axios.get(`https://amora-backend.onrender.com/api/student/decks`);
      const found = res.data.find((d) => d._id === deckId);
      setDeck(found);
    } catch (err) {
      console.error('Error loading deck:', err);
    }
  };

  useEffect(() => {
    if (deckId) fetchDeck();
  }, [deckId]);

  const handleCardClick = async (card) => {
    setSelectedCard(card);
    setLoading(true);
    setSummary({});
    try {
      const res = await axios.post('https://amora-ai-service.onrender.com/research-assistant', {
        title: card.title,
        description: card.description,
      });
      setSummary(res.data);
    } catch (err) {
      setSummary({ ai_summary: '⚠️ Failed to fetch insights.' });
    }
    setLoading(false);
  };

  const handleRemoveFromDeck = async (eventId) => {
    try {
      const res = await axios.delete(`https://amora-backend.onrender.com/api/student/decks/${deckId}/events/${eventId}`);
      if (res.data.success) {
        await fetchDeck();
      } else {
        console.warn('Deck deletion failed:', res.data);
      }
    } catch (err) {
      console.error('Error removing card:', err);
    }
  };
  

  if (!deck) return <div className="text-black p-4">Loading deck...</div>;

  return (
    <div className="fixed inset-0 bg-white bg-opacity-95 overflow-y-auto z-50 p-6 text-black">
      <button
        onClick={() => {
          setSelectedCard(null);
          setSummary({});
          onBack();
        }}
        className="absolute top-4 right-6 text-2xl font-bold text-black"
      >
        ✖
      </button>

      <h2 className="text-3xl font-extrabold uppercase border-b border-black mb-6 pb-2">{deck.title}</h2>

      <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
        {deck.events.map((event) => (
          <div
            key={event._id}
            onClick={() => handleCardClick(event)}
            className="relative group border border-black bg-white text-black hover:bg-black hover:text-white p-4 transition font-bold tracking-wide uppercase cursor-pointer"
          >
            <div>
              <h3 className="text-xl mb-1 group-hover:text-white">{event.title}</h3>
              <p className="text-sm mb-1 group-hover:text-white">{event.year}</p>
              <p className="text-sm mb-1 group-hover:text-white">{event.category}</p>
              <p className="text-sm line-clamp-3 group-hover:text-white">{event.description}</p>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFromDeck(event._id);
              }}
              className="absolute top-2 right-2 bg-white text-red-600 text-xs font-bold px-2 rounded hover:text-red-800"
            >
              ✖
            </button>
          </div>
        ))}
      </div>

      {selectedCard && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 px-4">
          <div className="bg-white text-black border border-black p-6 max-w-lg w-full relative shadow-none rounded-none overflow-y-auto max-h-[90vh]">
            <button
              onClick={() => {
                setSelectedCard(null);
                setSummary({});
              }}
              className="absolute top-3 right-4 text-black text-xl font-bold"
            >
              ✖
            </button>

            <h3 className="text-xl font-bold uppercase border-b border-black mb-2">{selectedCard.title}</h3>
            <p className="text-sm mb-1">{selectedCard.year}</p>
            <p className="text-sm mb-1">{selectedCard.category}</p>
            <p className="text-sm mt-3">{selectedCard.description}</p>

            {selectedCard.summary && (
              <>
                <h4 className="text-lg font-semibold mt-6">Notes</h4>
                <p className="text-sm whitespace-pre-wrap mt-2">{selectedCard.summary}</p>
              </>
            )}

            {loading ? (
              <p className="text-gray-600 text-sm mt-4">⏳ Loading insights...</p>
            ) : summary.ai_summary ||
              summary.bullet_points ||
              summary.did_you_know ||
              summary.related_links ? (
              <>
                {summary.ai_summary && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold">AI Summary</h4>
                    <p className="text-sm whitespace-pre-wrap">{summary.ai_summary}</p>
                  </div>
                )}
                {summary.bullet_points && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold">🔹 Bullet Points</h4>
                    <ul className="list-disc list-inside text-sm">
                      {summary.bullet_points.map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {summary.did_you_know && (
                  <p className="text-sm italic mt-4">🤔 Did you know? {summary.did_you_know}</p>
                )}
                {summary.related_links && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold">🌐 Resources</h4>
                    <ul className="list-disc list-inside text-sm">
                      {summary.related_links.map((link, idx) => (
                        <li key={idx}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-black"
                          >
                            {link.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <p className="text-red-400 text-sm mt-4">⚠️ No insights available.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckDetail;
